import * as XLSX from 'xlsx-js-style';
import type { OpenBacklogDetailExportKind, OpenBacklogDetailRow } from './kpiDashboardApi';
import { workbookToXlsxBlob } from '../../utils/workbookToXlsxBlob';
import {
  type DateFormatPreferences,
  DEFAULT_PREFERENCES,
  excelDateTimeNumberFormat,
  isoToExcelSerialUtc,
} from '../../utils/dateFormatter';
import { applyYellowHeaderRowAndAutofilter, setSheetColumnWidths } from './kpiDashboardExcelExport';

/**
 * Column layout aligned with legacy “Incident & Request Ageing” detail sheets.
 * L = Last Updated Duration, N = Duration (Excel formulas using NOW()).
 */
const HEADERS = [
  'NUMBER',
  'SHORT DESCRIPTION',
  'TYPE',
  'PRIORITY',
  'STATE',
  'ASSIGNMENT GROUP',
  'ASSIGNED TO',
  'COMPANY',
  'LAST UPDATED TIME',
  'LAST UPDATED BY',
  'TICKET CREATED',
  'Last Updated Duration',
  'Last Updated Ageing',
  'Duration',
  'Ageing',
  'Remarks',
] as const;

/** Column widths (A–P) aligned with reference: wide description & remarks; narrow priority/state/duration. */
const DETAIL_COLUMN_WIDTHS: number[] = [
  14, // NUMBER
  56, // SHORT DESCRIPTION
  12, // TYPE
  14, // PRIORITY
  12, // STATE
  32, // ASSIGNMENT GROUP
  22, // ASSIGNED TO
  30, // COMPANY
  20, // LAST UPDATED TIME
  24, // LAST UPDATED BY
  20, // TICKET CREATED
  18, // Last Updated Duration
  18, // Last Updated Ageing
  8, // Duration
  14, // Ageing
  48, // Remarks
];

function lastUpdatedDurationFormula(row: number): string {
  return `IF(NOW()<=I${row},"00 00:00:00",TEXT(INT(NOW()-I${row}),"00")&" "&TEXT(MOD(NOW()-I${row},1),"hh:mm:ss"))`;
}

function openDurationDaysFormula(row: number): string {
  return `MAX(0,INT(NOW()-K${row}))`;
}

/** Ageing bucket derived from Duration column N (must stay in sync with backend ageingBucketLabel). */
function openAgeingBucketFormula(row: number): string {
  const n = `N${row}`;
  return `IF(${n}<2,"< 2 days",IF(${n}<=5,"2-5 Days",IF(${n}<=10,"6-10 Days",IF(${n}<=15,"11-15 Days",IF(${n}<=30,"16-30 Days",">30 Days")))))`;
}

export function buildOpenBacklogDetailExcel(
  rows: OpenBacklogDetailRow[],
  kind: OpenBacklogDetailExportKind,
  preferences: DateFormatPreferences = DEFAULT_PREFERENCES
): { blob: Blob; fileName: string } {
  const dateFmt = excelDateTimeNumberFormat(preferences);
  const aoa: (string | number)[][] = [[...HEADERS]];
  for (const r of rows) {
    aoa.push([
      r.number,
      r.shortDescription,
      r.type,
      r.priority,
      r.state,
      r.assignmentGroup,
      r.assignedTo ?? '',
      r.company ?? '',
      isoToExcelSerialUtc(r.lastUpdatedTime),
      r.lastUpdatedBy ?? '',
      isoToExcelSerialUtc(r.ticketCreated),
      '',
      r.lastUpdatedAgeing,
      '',
      '',
      r.remarks,
    ]);
  }

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const colCount = HEADERS.length;
  const rowCount = aoa.length;

  for (let i = 0; i < rows.length; i++) {
    const excelRow = i + 2;
    const iAddr = `I${excelRow}`;
    const kAddr = `K${excelRow}`;
    const lAddr = `L${excelRow}`;
    const nAddr = `N${excelRow}`;
    const oAddr = `O${excelRow}`;

    if (ws[iAddr] && ws[iAddr].v !== '' && ws[iAddr].v !== undefined) {
      ws[iAddr].z = dateFmt;
    }
    if (ws[kAddr] && ws[kAddr].v !== '' && ws[kAddr].v !== undefined) {
      ws[kAddr].z = dateFmt;
    }

    ws[lAddr] = { f: lastUpdatedDurationFormula(excelRow) };
    ws[nAddr] = { f: openDurationDaysFormula(excelRow) };
    ws[oAddr] = { f: openAgeingBucketFormula(excelRow) };
  }

  applyYellowHeaderRowAndAutofilter(ws, colCount, rowCount);
  setSheetColumnWidths(ws, DETAIL_COLUMN_WIDTHS);

  const wb = XLSX.utils.book_new();
  const sheetName = kind === 'ageing' ? 'Ageing Report' : 'Last updated detail';
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  const stamp = new Date().toISOString().slice(0, 10);
  const slug = kind === 'ageing' ? 'open_backlog_ageing_detail' : 'open_backlog_last_updated_detail';
  const fileName = `kpi_${slug}_${stamp}.xlsx`;
  return { blob: workbookToXlsxBlob(wb), fileName };
}
